import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { ProductionCategory } from '../Context/types';
import { httpClient } from './appDependenciesHelpers';

export type ProductionCategoryQueryInput = {
  currentUserEmail: string | null | undefined;
  token: string;
  customerId: number;
  varietyId: number | null;
  forceInvalidateQuery?: boolean;
};

const useProductionCategoryQuery = (
  input: ProductionCategoryQueryInput
): UseQueryResult<ProductionCategory[], Error> => {
  const { currentUserEmail, forceInvalidateQuery, token, varietyId, customerId } = input;
  const productionCategoriesQueryKeys =
    (currentUserEmail === null || currentUserEmail === undefined) && varietyId === null
      ? ['production_category']
      : ['production_category', currentUserEmail, varietyId, customerId];

  async function getProductionCategories(): Promise<ProductionCategory[]> {
    let prodCategories: ProductionCategory[] = [];
    if (token) {
      const BASE_URL = `${process.env.REACT_APP_BE_URL}`;
      const res = await httpClient
        .getAxios()
        .get(`${BASE_URL}/varieties/${varietyId}/production_categories`, {
          headers: {
            Authorization: token
          },
          params: { customer_id: customerId }
        })
        .catch(() => {});
      if (res?.data && res.data.length) {
        prodCategories = res.data;
      }
    }
    return prodCategories.sort((a, b) => {
      // Handle "unknown" items - put them at the end
      const isAUnknown = a.name.toLowerCase().includes('unknown');
      const isBUnknown = b.name.toLowerCase().includes('unknown');

      if (isAUnknown && !isBUnknown) return 1; // a goes after b
      if (!isAUnknown && isBUnknown) return -1; // a goes before b
      if (isAUnknown && isBUnknown) {
        // If both are unknown, sort them alphabetically
        return a.name.localeCompare(b.name);
      }

      // Extract numeric values from strings like "10cm", "30cm", etc.
      const numA = parseInt(a.name);
      const numB = parseInt(b.name);

      // Compare numerically
      return numA - numB;
    });
  }

  const productionCategoryQuery = useQuery({
    queryKey: productionCategoriesQueryKeys,
    queryFn: getProductionCategories,
    enabled:
      token !== '' &&
      (currentUserEmail !== null || currentUserEmail !== undefined) &&
      varietyId !== null &&
      customerId !== 0
  });

  if (forceInvalidateQuery) {
    productionCategoryQuery.refetch();
  }

  return productionCategoryQuery;
};

export default useProductionCategoryQuery;
